import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ROUTES } from './utils/constants';

// Pages
import Home from './pages/Home';
import UserLogin from './pages/UserLogin';
import UserRegister from './pages/UserRegister';
import UserDashboard from './pages/UserDashboard';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import ModernAdminDashboard from './pages/ModernAdminDashboard';
import ManageVideos from './pages/admin/ManageVideos';
import ManageGallery from './pages/admin/ManageGallery';
import ManageProducts from './pages/admin/ManageProducts';
import DownloadToken from './pages/DownloadToken';
import TermsAndConditions from './pages/TermsAndConditions';
import Disclaimer from './pages/Disclaimer';
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to={adminOnly ? ROUTES.ADMIN_LOGIN : ROUTES.LOGIN} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children, redirectTo = ROUTES.HOME }) => {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (currentUser) {
    // If user is admin, redirect to admin dashboard
    if (isAdmin) {
      return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
    }
    // Otherwise redirect to specified route (user dashboard)
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path={ROUTES.HOME} element={<Home />} />
          <Route path={ROUTES.TOKEN_DOWNLOAD} element={<DownloadToken />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          
          {/* Auth Routes (redirect if logged in) */}
          <Route 
            path={ROUTES.LOGIN} 
            element={
              <PublicRoute redirectTo={ROUTES.HOME}>
                <UserLogin />
              </PublicRoute>
            } 
          />
          <Route 
            path={ROUTES.REGISTER} 
            element={
              <PublicRoute redirectTo={ROUTES.HOME}>
                <UserRegister />
              </PublicRoute>
            } 
          />
          <Route 
            path={ROUTES.ADMIN_LOGIN} 
            element={
              <PublicRoute redirectTo={ROUTES.ADMIN_DASHBOARD}>
                <AdminLogin />
              </PublicRoute>
            } 
          />

          {/* Protected User Routes */}
          <Route 
            path={ROUTES.USER_DASHBOARD} 
            element={
              <ProtectedRoute>
                <UserDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Protected Admin Routes */}
          <Route 
            path={`${ROUTES.ADMIN_DASHBOARD}/*`} 
            element={
              <ProtectedRoute adminOnly={true}>
                <ModernAdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/videos" 
            element={
              <ProtectedRoute adminOnly={true}>
                <ManageVideos />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin/gallery" 
            element={
              <ProtectedRoute adminOnly={true}>
                <ManageGallery />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/admin/products" 
            element={
              <ProtectedRoute adminOnly={true}>
                <ManageProducts />
              </ProtectedRoute>
            } 
          />
          
          {/* Old Admin Dashboard (fallback) */}
          <Route 
            path="/admin/dashboard-old" 
            element={
              <ProtectedRoute adminOnly={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
